import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  AlertTriangle,
  CheckCircle2,
  Shield,
  Brain,
  Scale,
  Lock,
  Sparkles,
  RefreshCw,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'
import FlipCard from '../components/Interactive/FlipCard'

interface SlideProps {
  isActive: boolean
}

const challenges = [
  {
    challenge: {
      icon: AlertTriangle,
      title: 'Hallucinations',
      description: 'AI models hallucinate 33-48% of the time. Package hallucinations can lead to malware installation.',
      severity: 'critical',
      source: 'arXiv Research 2024',
    },
    solution: {
      icon: CheckCircle2,
      title: 'Validation & Testing',
      points: [
        'Verify all packages and APIs exist',
        'Run code through execution testing',
        'Cross-reference official documentation',
        'Use type checking and linters',
      ],
    },
  },
  {
    challenge: {
      icon: Shield,
      title: 'Security Vulnerabilities',
      description: '45% of AI-generated code contains OWASP Top 10 vulnerabilities. Java has 72% security failure rate.',
      severity: 'critical',
      source: 'Veracode 2025',
    },
    solution: {
      icon: Lock,
      title: 'Security-First Approach',
      points: [
        'Run SAST/DAST security scanners',
        'Never hardcode secrets or credentials',
        'Review auth & input validation carefully',
        'Follow OWASP LLM Top 10 2025',
      ],
    },
  },
  {
    challenge: {
      icon: Brain,
      title: 'Context Limitations',
      description: '65% of developers report AI misses critical context. "Context rot" degrades output quality over long sessions.',
      severity: 'high',
      source: 'Stack Overflow 2025',
    },
    solution: {
      icon: Sparkles,
      title: 'Rich Context Provision',
      points: [
        'Include relevant code snippets',
        'Explain architectural patterns',
        'Start fresh sessions for new tasks',
        'Use tools with codebase indexing',
      ],
    },
  },
  {
    challenge: {
      icon: Scale,
      title: 'Over-reliance Risk',
      description: 'Trust in AI output dropped to 33%. Junior developer skill degradation is a top industry concern.',
      severity: 'high',
      source: 'Stack Overflow 2025',
    },
    solution: {
      icon: RefreshCw,
      title: 'Balanced Workflow',
      points: [
        'Use AI as learning & pairing tool',
        'Understand every line before accepting',
        'Practice coding without AI regularly',
        'Focus on architecture & design skills',
      ],
    },
  },
]

export default function ChallengesSolutionsSlide({ isActive: _isActive }: SlideProps) {
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set())

  const toggleCard = (index: number) => {
    const newFlipped = new Set(flippedCards)
    if (newFlipped.has(index)) {
      newFlipped.delete(index)
    } else {
      newFlipped.add(index)
    }
    setFlippedCards(newFlipped)
  }

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="mb-6 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-3">
              Challenges <span className="text-gradient">&</span> Solutions
            </h2>
            <p className="text-lg text-white/60">
              Click cards to reveal solutions to common AI coding challenges
            </p>
          </div>
        </StaggerItem>

        {/* Cards Grid */}
        <StaggerItem className="flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
            {challenges.map((item, index) => {
              const ChallengeIcon = item.challenge.icon
              const SolutionIcon = item.solution.icon

              return (
                <motion.div
                  key={item.challenge.title}
                  initial={{ opacity: 0, rotateY: -15 }}
                  animate={{ opacity: 1, rotateY: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="h-[200px] perspective-1000"
                >
                  <FlipCard
                    className="h-full w-full"
                    front={
                      <div
                        className={`h-full glass rounded-2xl p-6 flex flex-col ${
                          item.challenge.severity === 'critical'
                            ? 'border-l-4 border-red-500'
                            : item.challenge.severity === 'high'
                            ? 'border-l-4 border-orange-500'
                            : 'border-l-4 border-yellow-500'
                        }`}
                        onClick={() => toggleCard(index)}
                      >
                        <div className="flex items-start gap-4">
                          <div
                            className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              item.challenge.severity === 'critical'
                                ? 'bg-red-500/20'
                                : item.challenge.severity === 'high'
                                ? 'bg-orange-500/20'
                                : 'bg-yellow-500/20'
                            }`}
                          >
                            <ChallengeIcon
                              className={`w-7 h-7 ${
                                item.challenge.severity === 'critical'
                                  ? 'text-red-500'
                                  : item.challenge.severity === 'high'
                                  ? 'text-orange-500'
                                  : 'text-yellow-500'
                              }`}
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-2xl font-semibold text-white">
                                {item.challenge.title}
                              </h3>
                              <span
                                className={`text-sm px-3 py-1 rounded-full ${
                                  item.challenge.severity === 'critical'
                                    ? 'bg-red-500/20 text-red-400'
                                    : item.challenge.severity === 'high'
                                    ? 'bg-orange-500/20 text-orange-400'
                                    : 'bg-yellow-500/20 text-yellow-400'
                                }`}
                              >
                                {item.challenge.severity}
                              </span>
                            </div>
                            <p className="text-lg text-white/60 leading-relaxed">
                              {item.challenge.description}
                            </p>
                            <p className="text-sm text-white/40 mt-2">
                              {item.challenge.source}
                            </p>
                          </div>
                        </div>
                      </div>
                    }
                    back={
                      <div
                        className="h-full glass rounded-2xl p-6 flex flex-col bg-gradient-to-br from-electric-cyan/10 to-electric-purple/10 border-l-4 border-electric-cyan"
                        onClick={() => toggleCard(index)}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-xl bg-electric-cyan/20 flex items-center justify-center flex-shrink-0">
                            <SolutionIcon className="w-7 h-7 text-electric-cyan" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-2xl font-semibold text-electric-cyan mb-3">
                              {item.solution.title}
                            </h3>
                            <ul className="space-y-2">
                              {item.solution.points.map((point, i) => (
                                <motion.li
                                  key={i}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.1 + i * 0.05 }}
                                  className="flex items-start gap-2 text-base text-white/70"
                                >
                                  <CheckCircle2 className="w-5 h-5 text-electric-cyan flex-shrink-0 mt-0.5" />
                                  {point}
                                </motion.li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    }
                  />
                </motion.div>
              )
            })}
          </div>
        </StaggerItem>

        {/* Progress indicator and sources */}
        <StaggerItem>
          <div className="mt-4 mb-[100px]">
            <div className="flex justify-center gap-2 mb-3">
              {challenges.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    flippedCards.has(index)
                      ? 'bg-electric-cyan'
                      : 'bg-white/20'
                  }`}
                />
              ))}
              <span className="ml-3 text-xs text-white/40">
                {flippedCards.size}/{challenges.length} explored
              </span>
            </div>
            <p className="text-xs text-white/40 text-center">
              Sources: arXiv Research • Veracode 2025 GenAI Security Report • Stack Overflow Developer Survey 2025 • OWASP
            </p>
          </div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
